import React, { useContext, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import UserTileContext from '../../../contexts/userTilesContext';

import { ICheckItem, IModalProps, IModuleTile, ISelectableModuleTile } from '../../../interfaces/interfaces';
import { availableModules, getModuleComponents } from '../../../modules';
import { loadStoreData } from '../../../utils/data';
import getNextFreeSlot from '../../../utils/get-next-free-slot';
import keyboardTriggerBasic from '../../../utils/keyboard-trigger';
import Accordion from '../Accordion/Accordion';
import SearchInput from '../Inputs/SearchInput';
import List from '../List/List';
import SelectableTilePreview from '../SelectableTilePreview/SelectableTilePreview';

function AppStoreModal(props: IModalProps): JSX.Element {
  const { onConfirm, onAbort } = props;
  const modalRef = useRef<HTMLAnchorElement>(null);
  const { t } = useTranslation();

  const userTiles = useContext(UserTileContext);

  const [searchString, setSearchString] = useState<string>('');
  const [filters, setFilters] = useState<ICheckItem[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [filteredTiles, setFilteredTiles] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [allTiles, setAllTiles] = useState<any[]>([]);

  const isSmall = useMediaQuery({ query: '(max-width: 600px)' });

  const allowMultipleTilesForModule = (moduleName: string): boolean => {
    return ['ContactModule'].indexOf(moduleName) >= 0;
  };

  const loadData = async () => {
    const isLocal = !process.env.NODE_ENV || process.env.NODE_ENV === 'development';
    const data = await loadStoreData(isLocal);

    const categories = data;
    let collectedTiles: ICheckItem[] = [];

    const activeTileIds = userTiles.tiles
      .map((ut) => {
        // if (allowMultipleTilesForModule(ut.module)) {
        //   return `${ut.tileRefId}_${Math.round(Math.random() * 10000)}`;
        // }

        return ut.tileRefId;
      })
      .filter((f) => f != null);

    collectedTiles = data
      .map((cat): ICheckItem[] => {
        // add categoryId to checkItem
        return ((cat.tiles || []) as IModuleTile[]).map((n) => {
          const isDisabled = allowMultipleTilesForModule(n.module) && activeTileIds.indexOf(n.tileRefId) > -1;
          return { ...n, categoryId: cat.id, title: '', isDisabled } as ICheckItem;
        });
      })
      .flat();

    const enrichedCollectedTiles = collectedTiles.map((ct) => {
      // use only refTiles for ids in refTileIds
      const tRef = ct.tileRefId || '';
      if (activeTileIds.indexOf(tRef as string) > -1) {
        return { ...ct, isSelected: true, isDisabled: ct.isDisabled };
      }
      return { ...ct, id: ct.tileRefId };
    });
    setFilters(categories);
    setAllTiles(enrichedCollectedTiles);
    setFilteredTiles(enrichedCollectedTiles);
  };

  const onAbortClick = () => {
    // close modal
    onAbort();
  };

  useEffect(() => {
    /**/
    loadData();
  }, []);

  const updateProducts = () => {
    // categories
    const activeFilters = filters.filter((f) => f.checked).map((f) => f.id);
    let filterResult = allTiles;
    if (activeFilters.length) {
      filterResult = allTiles.filter((tile: ISelectableModuleTile) => {
        return activeFilters.indexOf(tile.categoryId || 'no-category') >= 0;
      });
    }
    // search
    if (searchString.length) {
      filterResult = filterResult.filter((tile: ISelectableModuleTile) => {
        const matchTitle = (tile.preview?.headline || '').toLowerCase().indexOf(searchString.toLowerCase()) >= 0;
        const matchProductRefId =
          (tile.productReference?.displayNumber || '').toLowerCase().indexOf(searchString.toLowerCase()) >= 0;
        const matchDescription =
          (tile.preview?.description || '').toLowerCase().indexOf(searchString.toLowerCase()) >= 0;
        return matchTitle || matchDescription || matchProductRefId;
      });
    }

    setFilteredTiles(filterResult);
  };

  const onFilterItemChange = (item: ICheckItem, checked: boolean) => {
    item.checked = checked;
    setFilters([...filters]);
  };

  const onSearch = (searchStr?: string) => {
    setSearchString(searchStr || '');
  };

  const usedFilters = filters.filter((f) => f.checked).length > 0;
  const usedSearch = searchString.length > 0;
  const showFilterResultCount = usedFilters || usedSearch;

  useEffect(() => {
    updateProducts();
  }, [filters, searchString, allTiles]);

  const refModules = getModuleComponents(availableModules);

  const onConfirmTileSelection = () => {
    // ------

    // add selected program tiles
    let newTilesFromProducts: IModuleTile[] = [];
    let withoutRemovedTiles: IModuleTile[] = userTiles.tiles;

    // collect
    allTiles.forEach((tile) => {
      if (tile.isSelected && !tile.isDisabled) {
        const refModule = refModules[tile.module];
        const w = tile.layout?.w || refModule.defaultLayout?.w || 1;
        const h = tile.layout?.h || refModule.defaultLayout?.h || 1;
        newTilesFromProducts.push({
          ...tile,
          id: allowMultipleTilesForModule(tile.module)
            ? `${tile.tileRefId}_${Math.round(Math.random() * 10000)}`
            : tile.tileRefId,
          layout: {
            i: tile.tileRefId,
            x: 0, // will be set later
            y: 0, // will be set later
            w,
            h,
          },
        });
        return;
      }
      withoutRemovedTiles = withoutRemovedTiles.filter((existingTile) => {
        if (allowMultipleTilesForModule(existingTile.module)) {
          return true;
        }
        return tile.tileRefId !== existingTile.tileRefId;
      });
    });

    // remove removed and readded tiles from newTiles to prevent duplication
    newTilesFromProducts = newTilesFromProducts.filter((nt) => {
      if (allowMultipleTilesForModule(nt.module)) {
        return true;
      }
      return withoutRemovedTiles.map((ot) => ot.tileRefId).indexOf(nt.tileRefId) === -1;
    });

    // all tiles to add are set: Calculate positions of new tiles in grid based on already existing tiles' position
    const newTiles: IModuleTile[] = [];
    newTilesFromProducts.forEach((tile) => {
      const currentTiles = [...withoutRemovedTiles, ...newTiles];
      const nextFreeSlot = getNextFreeSlot(currentTiles, tile.layout.w);
      tile.layout = { ...tile.layout, ...nextFreeSlot };

      newTiles.push(tile);
    });

    // -----
    const updatedSet = [...withoutRemovedTiles, ...newTiles];

    // update tileset
    userTiles.setTiles(updatedSet);
    // close modal
    onConfirm();
  };

  const onSelectProgram = (tile: ISelectableModuleTile, state: boolean) => {
    const selectedllTiles = allTiles.map((aT: ISelectableModuleTile) => {
      if (aT.tileRefId === tile.tileRefId) {
        aT.isSelected = state;
      }
      return aT;
    });
    setAllTiles(selectedllTiles);
  };

  const renderItems = () => {
    return filteredTiles.map((tile: IModuleTile) => {
      return (
        <SelectableTilePreview
          tile={tile}
          key={`stp-${tile.tileRefId}`}
          displayNumber={tile.productReference?.displayNumber || ''}
          onChange={onSelectProgram}
          isDisabled={tile.isDisabled}
        />
      );
    });
  };

  const resetFilters = () => {
    setFilters(
      filters.map((f) => {
        f.checked = false;
        return f;
      }),
    );
  };

  const closeFilters = () => {
    // close accordion via click
    document.querySelector('.app-store-modal .accordion-opener')?.dispatchEvent(
      new MouseEvent('click', {
        view: window,
        bubbles: true,
        cancelable: true,
        buttons: 1,
      }),
    );
  };

  const smallFilterButtons = () => {
    return filters
      .filter((f) => f.checked)
      .map((f) => {
        return (
          <a
            key={`fi-${f.id}`}
            role="button"
            className="link--button link--button-secondary remove-filter-button"
            onClick={() => {
              onFilterItemChange(f, false);
            }}
            onKeyDown={(e) => {
              keyboardTriggerBasic(e, () => {
                onFilterItemChange(f, false);
              });
            }}
            tabIndex={0}
          >
            <span className="ic-close" />
            <span className="button-name">{f.title}</span>
          </a>
        );
      });
  };

  const resetFilterButton = usedFilters ? (
    <a
      role="button"
      className="link reset-filters"
      onClick={resetFilters}
      onKeyDown={(e) => {
        keyboardTriggerBasic(e, () => {
          resetFilters();
        });
      }}
      tabIndex={0}
    >
      <span className="ic-close" />
      <span className="button-name">
        <Trans i18nKey="cockpit.appstore.resetFilters">Filter zurücksetzen</Trans>
      </span>
    </a>
  ) : (
    ''
  );

  const selectModulesButton = (
    <div>
      <a
        role="button"
        className="link--button confirm-filter-button"
        onClick={closeFilters}
        onKeyDown={(e) => {
          keyboardTriggerBasic(e, () => {
            closeFilters();
          });
        }}
        tabIndex={0}
      >
        <span className="ic-arrowRight" />
        <span className="button-name">
          <Trans i18nKey="cockpit.appstore.confirmFilters">Module anzeigen</Trans>
        </span>
      </a>
    </div>
  );

  const filterColumn = (
    <div>
      <List
        items={filters.filter((f) => {
          return f.tiles?.length;
        })}
        isChecklist
        onItemChange={onFilterItemChange}
        itemClassName="scroll-inset"
      />
      {isSmall && selectModulesButton}
      {resetFilterButton}
    </div>
  );

  const resultsColumn = (
    <div className="item-list program-item-list">
      <div className="program-item-list">
        <ul className="item-list program-tile-list">{renderItems()}</ul>
      </div>
    </div>
  );

  const searchContainer = (
    <div className="">
      <div className="search-box ">
        <div className="inner u-clearfix">
          <h3 className="hl-3">
            <Trans i18nKey="cockpit.appstore.searchHeadline">Modul Suchen</Trans>
          </h3>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="search-label col-xs-1-4 txt_4" htmlFor="app-store-search-input">
            <Trans i18nKey="cockpit.appstore.searchHint">Produktnummer oder Stichwort eingeben</Trans>
          </label>
          <div className="input-holder col-sm-3-4">
            <SearchInput
              className="input default-input search-input"
              inputName="query"
              onSubmit={(input) => {
                onSearch(input.current?.value);
              }}
              onChange={(value) => {
                onSearch(value);
              }}
              id="app-store-search-input"
            />
          </div>

          {isSmall && (
            <Accordion
              title={t('cockpit.appstore.categoryAccordionOpener', 'Kategorien auswählen')}
              contentCount={filters.filter((f) => f.checked).length}
            >
              <div className="accordion-inner">
                <div className="hl-5">
                  <Trans i18nKey="cockpit.appstore.searchResultCountSmall" count={filteredTiles.length}>
                    Insgesamt <span className="count-badge">{`${filteredTiles.length}`}</span> Module
                  </Trans>
                </div>
                {filterColumn}
              </div>
            </Accordion>
          )}
        </div>
      </div>
    </div>
  );

  const modalActions = (
    <div className="modal-actions">
      <a
        role="button"
        className="link--button link--button-frameless"
        onClick={onAbortClick}
        onKeyDown={(e) => {
          keyboardTriggerBasic(e, onAbortClick);
        }}
        tabIndex={0}
      >
        <span className="ic-timeout" />
        <span className="button-name">
          <Trans i18nKey="cockpit.appstore.abortButton">Später festlegen</Trans>
        </span>
      </a>
      <a
        role="button"
        className="link--button"
        onClick={onConfirmTileSelection}
        onKeyDown={(e) => {
          keyboardTriggerBasic(e, onConfirmTileSelection);
        }}
        tabIndex={0}
        ref={modalRef}
      >
        <span className="ic-arrowRight" />
        <span className="button-name">
          <Trans i18nKey="cockpit.appstore.confirmButton">Zum Cockpit hinzufügen</Trans>
        </span>
      </a>
    </div>
  );

  return (
    <>
      <div className="modal app-store-modal" role="dialog">
        <div className="modal-inner l-container">
          <a
            role="button"
            className="modal-closer"
            onClick={onAbortClick}
            onKeyDown={(e) => {
              keyboardTriggerBasic(e, onAbortClick);
            }}
            tabIndex={0}
          >
            <span className="ic-close" />
          </a>

          <div className="modal-inner-content">
            <div className="scrollable-content">
              <h3 className="hl-2">
                <Trans i18nKey="cockpit.appstore.headline">Neues Modul zum Cockpit hinzufügen</Trans>
              </h3>
              <div className="intro-text tx_4" dangerouslySetInnerHTML={{ __html: t('cockpit.appstore.intro') }} />
              {searchContainer}

              <div className="u-clearfix scroll-inset">
                <aside className="col-xs-1-4 cat-col">
                  <h4 className="hl-4 scroll-inset">
                    <Trans i18nKey="cockpit.appstore.categoryHeadline">Kategorien</Trans>
                  </h4>
                  <div className="s-content">
                    <div className="">{filterColumn}</div>
                  </div>
                </aside>

                <div className="col-xs-3-4 products-col">
                  {isSmall && smallFilterButtons()}
                  <h4 className="hl-4">
                    <Trans i18nKey="cockpit.appstore.searchResultHeadline">Module auswählen</Trans>{' '}
                    {showFilterResultCount ? (
                      <Trans i18nKey="cockpit.appstore.searchResultCount" count={filteredTiles.length}>
                        - Ihre Suche ergab <span>{`${filteredTiles.length}`}</span> Treffer
                      </Trans>
                    ) : (
                      ''
                    )}
                  </h4>
                  <div className="s">{resultsColumn}</div>
                </div>
              </div>
              {modalActions}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
export default AppStoreModal;
